import React, { useEffect, useState } from 'react';
import { X, Check, Package, Tag, Timer, Building2, AlertTriangle } from 'lucide-react';
import ShimmerImage from './ShimmerImage';
import { effectiveDealPriceCents } from '../services/dealPricing';
import { billingApi, type BillingSkuRow } from '../services/billingApi';
import { hardwareApi, type DeviceBundleRow, type DeviceModelRow } from '../services/hardwareApi';

import { RubyGemMark } from './RubyGemMark';
/**
 * Deal picker — a small modal that shows what each deal actually GIVES a
 * referral (price, trial, locations, packages, and the devices with their
 * pictures) and assigns the one that is clicked.
 *
 * It replaced a bare `<select>` whose only label was "name — $x/mo": the deal
 * decides what a partner earns from, so the choice is worth showing properly.
 * Device artwork is resolved the same way `DealEditPage` resolves it —
 * bundle hero image, else model hero image — so a deal looks identical in the
 * picker and in the editor.
 */

export interface PickerDeal {
  id: string;
  name: string;
  code: string;
  monthlyAmount?: number;
  priceCents?: number;
  trialDays: number;
  commissionMode: 'flat' | 'percentage' | 'both';
  flatFee?: number;
  percentageRate?: number;
  isDefaultDeal?: boolean;
  freeLocationQuota?: number;
  additionalLocationPriceCents?: number;
  packageIds?: string[];
  deviceSkuIds?: string[];
  devicePackageIds?: string[];
}

interface Props {
  open: boolean;
  deals: PickerDeal[];
  /** Currently assigned deal id, or '' for none. */
  currentDealId: string;
  /** Assign and close. `null` clears the assignment. */
  onSelect: (dealId: string | null) => Promise<void> | void;
  onClose: () => void;
}

const money = (cents: number): string =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const commissionLabel = (deal: PickerDeal): string => {
  if (deal.commissionMode === 'percentage') return `${deal.percentageRate ?? 0}%`;
  if (deal.commissionMode === 'flat') return money(deal.flatFee || 0);
  return `${deal.percentageRate ?? 0}% + ${money(deal.flatFee || 0)}`;
};

const saleModeLabel = (sku: BillingSkuRow): string => {
  switch (sku.saleMode) {
    case 'outright': return `${money(sku.amountCents)} one-time`;
    case 'installment': return `${money(sku.amountCents)}/mo × ${sku.termMonths ?? '?'}`;
    case 'rental': return `${money(sku.amountCents)}/mo, no term`;
    case 'lease': return `${money(sku.amountCents)}/mo lease`;
    case 'included': return 'Included free';
    default: return money(sku.amountCents);
  }
};

const DealPickerModal: React.FC<Props> = ({ open, deals, currentDealId, onSelect, onClose }) => {
  const [skus, setSkus] = useState<BillingSkuRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [hardwareError, setHardwareError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  // Device artwork is enrichment: a failure must not stop the picker working,
  // but it has to be VISIBLE rather than silently showing deals with no devices.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [skuRes, bundleRes, modelRes] = await Promise.all([
          billingApi.listSkus(),
          hardwareApi.listBundles(),
          hardwareApi.listModels(),
        ]);
        if (cancelled) return;
        setSkus((skuRes.items || []).filter((s) => s.type === 'device_finance'));
        setBundles(bundleRes.items || []);
        setModels(modelRes.items || []);
        setHardwareError(null);
      } catch (e: any) {
        if (!cancelled) setHardwareError(e?.message || 'Could not load device artwork');
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Escape closes — but never mid-save, which would leave the page showing a
  // deal that may or may not have been assigned.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const handlePick = async (dealId: string | null) => {
    if (saving) return;
    setSaving(dealId ?? '__none__');
    try {
      await onSelect(dealId);
      onClose();
    } finally {
      setSaving(null);
    }
  };

  const devicesFor = (deal: PickerDeal): BillingSkuRow[] => {
    const ids = deal.deviceSkuIds?.length ? deal.deviceSkuIds : (deal.devicePackageIds || []);
    return ids.map((id) => skus.find((s) => s.skuId === id)).filter(Boolean) as BillingSkuRow[];
  };

  const artwork = (sku: BillingSkuRow) => {
    const bundle = sku.bundleId ? bundles.find((b) => b.bundleId === sku.bundleId) : undefined;
    const model = sku.modelId ? models.find((m) => m.modelId === sku.modelId) : undefined;
    return {
      title: bundle?.name || model?.modelName || sku.name,
      image: bundle?.heroImageUrl || model?.media?.heroImageUrl,
    };
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => !saving && onClose()} />

      <div className="relative w-full max-w-2xl max-h-[86vh] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h3 className="text-lg font-semibold text-stone-800">Choose a deal</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              What a referral signs up on — and what this partner earns from it.
            </p>
          </div>
          <button
            onClick={() => !saving && onClose()}
            className="p-2 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {hardwareError && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{hardwareError} — deals still assign correctly, but device pictures are missing.</span>
            </div>
          )}

          {/* Clear the assignment. */}
          <button
            onClick={() => handlePick(null)}
            disabled={Boolean(saving)}
            className={`w-full text-left rounded-xl border-2 p-3 transition disabled:opacity-60 ${
              !currentDealId ? 'border-rose-400 bg-rose-50' : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-stone-700">No deal assigned</span>
              {saving === '__none__'
                ? <RubyGemMark size={16} drawing />
                : !currentDealId && <Check className="w-4 h-4 text-rose-600" />}
            </div>
            <p className="text-xs text-stone-500 mt-0.5">
              Referrals fall back to the platform default deal.
            </p>
          </button>

          {deals.map((deal) => {
            const isCurrent = deal.id === currentDealId;
            const devices = devicesFor(deal);
            return (
              <button
                key={deal.id}
                onClick={() => handlePick(deal.id)}
                disabled={Boolean(saving)}
                className={`w-full text-left rounded-xl border-2 p-4 transition disabled:opacity-60 ${
                  isCurrent ? 'border-rose-400 bg-rose-50' : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-stone-900">{deal.name}</span>
                      {deal.isDefaultDeal && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                          Default
                        </span>
                      )}
                      {isCurrent && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-600 text-white">
                          ASSIGNED
                        </span>
                      )}
                    </div>
                    <code className="text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                      {deal.code}
                    </code>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold text-stone-900 tabular-nums">
                      {money(effectiveDealPriceCents(deal))}
                    </div>
                    <div className="text-[11px] text-stone-500">per month</div>
                  </div>
                </div>

                {/* Terms */}
                {/* A "Trial" tile led this row until trials retired 2026-08-28 — the offer is price phases on packages and devices. */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                  <div className="rounded-lg bg-white border border-stone-200 px-2.5 py-2">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-stone-400">
                      <Building2 className="w-3 h-3" /> Locations
                    </div>
                    <div className="text-xs font-medium text-stone-800 mt-0.5">
                      {typeof deal.freeLocationQuota === 'number' ? deal.freeLocationQuota : '—'}
                      {typeof deal.additionalLocationPriceCents === 'number' && (
                        <span className="text-stone-400 font-normal">
                          {' '}+{money(deal.additionalLocationPriceCents)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white border border-stone-200 px-2.5 py-2">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-stone-400">
                      <Package className="w-3 h-3" /> Packages
                    </div>
                    <div className="text-xs font-medium text-stone-800 mt-0.5">
                      {deal.packageIds?.length || 0}
                    </div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-2">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-600">
                      <Tag className="w-3 h-3" /> Commission
                    </div>
                    <div className="text-xs font-semibold text-emerald-700 mt-0.5">
                      {commissionLabel(deal)}
                    </div>
                  </div>
                </div>

                {/* Devices offered with this deal */}
                {devices.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wide text-stone-400 mb-1.5">
                      Devices offered
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {devices.map((sku) => {
                        const { title, image } = artwork(sku);
                        return (
                          <div
                            key={sku.skuId}
                            className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white pr-2.5"
                          >
                            <ShimmerImage
                              src={image}
                              alt={title}
                              width={40}
                              height={40}
                              objectFit="contain"
                              className="rounded-l-lg shrink-0"
                              fallbackBgColor="bg-stone-900"
                              fallbackIcon={<Package size={14} className="text-stone-500" />}
                              variant="dark"
                            />
                            <div className="py-1">
                              <div className="text-[11px] font-medium text-stone-800 leading-tight">{title}</div>
                              <div className="text-[10px] text-stone-500 leading-tight">{saleModeLabel(sku)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {saving === deal.id && (
                  <div className="flex items-center gap-2 text-xs text-stone-500 mt-3">
                    <RubyGemMark size={14} drawing color="currentColor" /> Assigning…
                  </div>
                )}
              </button>
            );
          })}

          {deals.length === 0 && (
            <p className="text-sm text-stone-500 text-center py-8">
              No active deals yet. Build one in Inventory &rarr; Sales &rarr; Deals.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealPickerModal;
